import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Salvaged from ui-sprocket src/components/Skeletons.tsx — reviewed 2026-09-21:
// `BoardSkeleton` dropped (no deal board in the shells); `role="status"` + `aria-live`
// added to each so assistive tech hears "Loading" once; `KeyValueSkeleton` added for the
// tenant/billing detail pages. `Skeleton` itself is re-exported themed (wave, 6px radius).
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import MuiSkeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
export { MuiSkeleton as Skeleton };
const busy = {
    role: 'status',
    'aria-busy': true,
    'aria-label': 'Loading',
    'aria-live': 'polite',
};
/** A table: header band plus `rows` rows of `columns` cells. */
export function TableSkeleton({ rows = 8, columns = 5 }) {
    return (_jsxs(Card, { ...busy, children: [_jsx(Box, { sx: {
                    px: 2,
                    py: 1.25,
                    bgcolor: 'background.subtle',
                    borderBottom: '1px solid',
                    borderColor: 'hairline',
                    display: 'flex',
                    gap: 3,
                }, children: Array.from({ length: columns }, (_, i) => (_jsx(MuiSkeleton, { width: i === 0 ? 120 : 80, height: 14 }, i))) }), Array.from({ length: rows }, (_, r) => (_jsxs(Box, { sx: {
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                    borderBottom: r < rows - 1 ? '1px solid' : 0,
                    borderColor: 'hairline2',
                }, children: [_jsxs(Stack, { direction: "row", spacing: 1.25, sx: { alignItems: 'center', width: 200 }, children: [_jsx(MuiSkeleton, { variant: "circular", width: 24, height: 24 }), _jsxs(Box, { sx: { flexGrow: 1 }, children: [_jsx(MuiSkeleton, { width: "70%", height: 14 }), _jsx(MuiSkeleton, { width: "45%", height: 11 })] })] }), Array.from({ length: columns - 1 }, (_, c) => (_jsx(MuiSkeleton, { width: c === columns - 2 ? 60 : 90 + ((r * 7 + c * 13) % 40), height: 14 }, c)))] }, r)))] }));
}
/** A list inside a card: `rows` two-line rows. */
export function ListSkeleton({ rows = 5, card = true }) {
    const body = (_jsx(Box, { ...(card ? {} : busy), children: Array.from({ length: rows }, (_, r) => (_jsxs(Box, { sx: {
                py: 1.25,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                borderTop: r > 0 ? '1px solid' : 0,
                borderColor: 'hairline2',
            }, children: [_jsx(MuiSkeleton, { variant: "rounded", width: 16, height: 16 }), _jsxs(Box, { sx: { flexGrow: 1 }, children: [_jsx(MuiSkeleton, { width: `${40 + ((r * 17) % 35)}%`, height: 14 }), _jsx(MuiSkeleton, { width: "30%", height: 11 })] }), _jsx(MuiSkeleton, { width: 48, height: 18 })] }, r))) }));
    return card ? (_jsx(Card, { ...busy, sx: { px: 2.5, py: 1 }, children: body })) : (body);
}
/** A card of prose or key–value lines. */
export function CardSkeleton({ lines = 4, title = true }) {
    return (_jsxs(Card, { ...busy, sx: { p: 2.5 }, children: [title && _jsx(MuiSkeleton, { width: 140, height: 16, sx: { mb: 1.5 } }), Array.from({ length: lines }, (_, i) => (_jsx(MuiSkeleton, { width: `${55 + ((i * 23) % 40)}%`, height: 13, sx: { mb: 1 } }, i)))] }));
}
/** A key–value list (a record's profile column) in the shape of the content. */
export function KeyValueSkeleton({ rows = 5 }) {
    return (_jsx(Box, { ...busy, sx: { display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 1.5, columnGap: 3 }, children: Array.from({ length: rows }, (_, i) => (_jsxs(Box, { sx: { display: 'contents' }, children: [_jsx(MuiSkeleton, { width: 80 + ((i * 11) % 40), height: 13 }), _jsx(MuiSkeleton, { width: `${35 + ((i * 19) % 45)}%`, height: 13 })] }, i))) }));
}
//# sourceMappingURL=skeleton.js.map